import React from 'react';

const TermsOfServicePage: React.FC = () => {
  return (
    <div className="p-6 max-w-4xl mx-auto bg-surface-card shadow-md rounded-xl mt-6">
      <h1 className="font-display tracking-tight text-3xl font-bold mb-6 text-text-primary">Terms of Service</h1>
      <p className="text-sm text-text-muted mb-6">Last Updated: February 12, 2026</p>

      <div className="space-y-4 text-text-body">
        <p>
          These Terms of Service ("Terms") govern your use of the Sync EHS platform (the "Platform"), which includes the web application, APIs, dashboards, reports, and related services provided by Sync EHS, LLC ("Sync EHS," "We," "Us," or "Our") for safety management. By creating an account or accessing the Platform, you agree to these Terms, as well as our Privacy Policy and License & Use Agreement.
        </p>

        <section>
          <h2 className="font-display text-xl font-semibold mb-2 text-text-body">1. Acceptance of Terms</h2>
          <p>
            By accessing or using the Platform, you agree to be bound by these Terms. If you do not agree, you may not use the Platform. These Terms apply to all users under your account, and you are responsible for ensuring their compliance.
          </p>
        </section>

        <section>
          <h2 className="font-display text-xl font-semibold mb-2 text-text-body">2. Use of the Platform</h2>
          <ul className="list-disc list-inside ml-4 space-y-1">
            <li>You may use the Platform only for lawful safety management purposes, such as tracking workplace incidents and generating safety reports.</li>
            <li>You agree not to use the Platform for any illegal, harmful, or unauthorized activities, including but not limited to spamming, uploading malicious content, or violating OSHA regulations.</li>
            <li>You must keep your login credentials secure and limit access to authorized users only. You are responsible for all activities under your account.</li>
          </ul>
        </section>

        <section>
          <h2 className="font-display text-xl font-semibold mb-2 text-text-body">3. Platform Limitations</h2>
          <ul className="list-disc list-inside ml-4 space-y-1">
            <li>Sync EHS provides tools for safety management but does not provide legal, compliance, or regulatory guarantees. Using the Platform does not guarantee OSHA compliance or prevent workplace injuries.</li>
            <li>You are solely responsible for interpreting the data, enforcing safety policies, training your employees, and ensuring compliance with applicable laws and regulations.</li>
            <li>Sync EHS is not liable for workplace incidents, safety outcomes, or regulatory violations arising from your use of the Platform.</li>
          </ul>
        </section>

        <section>
          <h2 className="font-display text-xl font-semibold mb-2 text-text-body">4. Intellectual Property and Restrictions on Use</h2>
          <p className="mb-2">
            <strong>Ownership.</strong> All rights, title, and interest in and to the SyncEHS platform, including but not limited to its source code, object code, software, algorithms, database structures, user interface designs, workflows, business logic, documentation, trade names, trademarks, service marks, logos, and all related intellectual property (collectively, the "Platform IP"), are and shall remain the sole and exclusive property of Sync EHS, LLC. Your use of the Platform does not grant you any ownership interest in the Platform IP. All rights not expressly granted herein are reserved by Sync EHS, LLC.
          </p>
          <p className="mb-2">
            <strong>Restrictions.</strong> You agree that you shall not, directly or indirectly, and shall not permit any third party to:
          </p>
          <ul className="list-disc list-inside ml-4 space-y-1">
            <li>(a) Reverse engineer, decompile, disassemble, or otherwise attempt to derive the source code, algorithms, data structures, or underlying ideas of the Platform;</li>
            <li>(b) Copy, reproduce, or replicate the Platform or any of its features, functions, modules, workflows, user interface designs, or data structures, in whole or in part;</li>
            <li>(c) Create derivative works based on the Platform or any part thereof, including any software, application, or service that is substantially similar to, imitates, or replicates the functionality of the Platform;</li>
            <li>(d) Use the Platform to develop any competing product or service, or assist any third party in doing so;</li>
            <li>(e) Remove, alter, or obscure any copyright, trademark, or other proprietary notices contained in or on the Platform;</li>
            <li>(f) Share, transfer, or sublicense your account credentials or access to the Platform to any person or entity not authorized by Sync EHS, LLC;</li>
            <li>(g) Use automated tools, including bots, scrapers, spiders, or crawlers, to access, index, or extract data or content from the Platform.</li>
          </ul>
        </section>

        <section>
          <h2 className="font-display text-xl font-semibold mb-2 text-text-body">5. Feedback and Suggestions</h2>
          <p>
            Any suggestions, ideas, enhancement requests, feedback, or recommendations you provide regarding the Platform ("Feedback") shall be the exclusive property of Sync EHS, LLC. You hereby assign all rights in any Feedback to Sync EHS, LLC and acknowledge that Sync EHS, LLC is free to use, incorporate, and commercialize such Feedback without restriction, attribution, or compensation to you.
          </p>
        </section>

        <section>
          <h2 className="font-display text-xl font-semibold mb-2 text-text-body">6. Confidentiality of Platform Features</h2>
          <p>
            You acknowledge that the Platform's features, functionality, user interfaces, pricing models, reporting logic, data architecture, and technical design constitute confidential and proprietary information of Sync EHS, LLC. You agree not to disclose details of the Platform's design, architecture, or proprietary workflows to any third party for the purpose of enabling the development of a competing product or service.
          </p>
        </section>

        <section>
          <h2 className="font-display text-xl font-semibold mb-2 text-text-body">7. User Conduct</h2>
          <ul className="list-disc list-inside ml-4 space-y-1">
            <li>You agree to use the Platform in compliance with all applicable laws, including OSHA regulations and data protection laws (e.g., GDPR, CCPA).</li>
            <li>You must not upload false, misleading, or malicious information, or engage in activities that disrupt the Platform (e.g., excessive API calls, denial-of-service attacks).</li>
            <li>You must promptly notify Sync EHS at support@syncehs.com of any security breaches or unauthorized access to your account.</li>
          </ul>
        </section>

        <section>
          <h2 className="font-display text-xl font-semibold mb-2 text-text-body">8. Account Suspension and Termination</h2>
          <ul className="list-disc list-inside ml-4 space-y-1">
            <li>Sync EHS may suspend or terminate your account if you violate these Terms, with notice where feasible.</li>
            <li>Examples of violations include non-payment, misuse of the Platform, or uploading illegal content.</li>
            <li>Upon termination, your data will be handled as described in the License & Use Agreement.</li>
          </ul>
        </section>

        <section>
          <h2 className="font-display text-xl font-semibold mb-2 text-text-body">9. Disclaimers</h2>
          <ul className="list-disc list-inside ml-4 space-y-1">
            <li>The Platform is provided "as-is" and "as-available," without warranties of any kind, express or implied, including but not limited to warranties of merchantability, fitness for a particular purpose, or non-infringement.</li>
            <li>Sync EHS does not guarantee uninterrupted or error-free access to the Platform and may perform maintenance or updates that temporarily affect availability.</li>
          </ul>
        </section>

        <section>
          <h2 className="font-display text-xl font-semibold mb-2 text-text-body">10. Limitation of Liability</h2>
          <ul className="list-disc list-inside ml-4 space-y-1">
            <li>Sync EHS's total liability under these Terms shall not exceed the amount you paid for the Platform in the preceding 12 months.</li>
            <li>Sync EHS shall not be liable for indirect, incidental, or consequential damages, including lost profits, business interruption, or data loss, arising from your use of the Platform.</li>
          </ul>
        </section>

        <section>
          <h2 className="font-display text-xl font-semibold mb-2 text-text-body">11. Indemnification</h2>
          <p>You agree to indemnify and hold harmless Sync EHS, its affiliates, officers, and employees from any claims, damages, or legal actions arising from:</p>
          <ul className="list-disc list-inside ml-4 space-y-1">
            <li>Your use of the Platform, including by your employees or agents.</li>
            <li>Any misuse, misinterpretation, or unauthorized use of data, graphs, dashboards, or reports generated by the Platform.</li>
          </ul>
        </section>

        <section>
          <h2 className="font-display text-xl font-semibold mb-2 text-text-body">12. Governing Law & Dispute Resolution</h2>
          <p>
            These Terms are governed by the laws of the State of Texas, without regard to conflict of law principles.
          </p>
          <p>
            Disputes arising under these Terms shall be resolved through binding arbitration in Harris County, TX, under the rules of the American Arbitration Association, unless otherwise agreed by both parties. Alternatively, disputes may be resolved in the state or federal courts of Harris County, TX, and both parties consent to this jurisdiction and venue.
          </p>
          <p>
            You waive any right to participate in a class action lawsuit against Sync EHS.
          </p>
          <p>
            <strong>Equitable Remedies.</strong> You acknowledge that any breach of the intellectual property, restrictions on use, or confidentiality provisions of these Terms would cause irreparable harm to Sync EHS, LLC for which monetary damages would be an inadequate remedy. In the event of such breach, Sync EHS, LLC shall be entitled to seek injunctive relief, specific performance, or other equitable remedies from any court of competent jurisdiction, without the necessity of posting a bond or proving actual damages, and without regard to any arbitration provision in these Terms.
          </p>
        </section>

        <section>
          <h2 className="font-display text-xl font-semibold mb-2 text-text-body">13. Changes to These Terms</h2>
          <p>
            We may update these Terms periodically. We will notify you of material changes via email or in-app notification at least 30 days before they take effect. Continued use of the Platform after the effective date constitutes acceptance of the updated Terms. If you do not agree to the changes, you may terminate your account before the effective date.
          </p>
        </section>

        <section>
          <h2 className="font-display text-xl font-semibold mb-2 text-text-body">14. Contact Us</h2>
          <p>If you have questions about these Terms, please contact us at:</p>
          <p>Email: <a href="mailto:support@syncehs.com" className="text-accent-blue hover:underline">support@syncehs.com</a></p>
          <p>We aim to respond to all inquiries within 1–2 business days.</p>
        </section>
      </div>
    </div>
  );
};

export default TermsOfServicePage;
